// src/components/tableHelpers/ColumnMenu.jsx
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Menu, MenuItem, InputBase, InputAdornment, ListSubheader } from "@mui/material";
import Search from "@mui/icons-material/Search";
import { MSwitchButton } from "../../M_Components";
import { useTranslation } from "react-i18next";

export default function ColumnMenu({ // Column Menu component that allows users to toggle visibility of table columns
    anchorEl,
    open,
    onClose,
    singleColumns = [], // Simple columns like ["Destination", "Min Loads"]
    columnGroups = [], // Groups like [{label: "Porto", children: ["Porto Bid", "Porto Best Bid"]}]
    selectedColumns,
    onChangeSelected,
    switchColor = "var(--color-red)",
    labelColor = "var(--color-black)",
}) {
    const { t } = useTranslation(); // Translation hook for internationalization
    const [query, setQuery] = useState("");

    //============================================================================================================
    // Sets filtered single columns based on the search query
    //============================================================================================================
    const filteredSingles = useMemo(() => { // Filters single columns based on the search query
        const rx = new RegExp(query, "i"); // Regular expression for case-insensitive search
        return singleColumns.filter((c) => rx.test(c)); // Filters single columns that match the query
    }, [singleColumns, query]);

    //============================================================================================================
    // Filters column groups based on the search query
    //============================================================================================================
    const filteredGroups = useMemo(() => { // Filters column groups based on the search query
        const rx = new RegExp(query, "i"); // Regular expression for case-insensitive search
        return columnGroups.filter((g) => rx.test(g.label)); // Filters groups that match the query
    }, [columnGroups, query]);

    //============================================================================================================
    // Checks if all children of a group are selected, and if so, returns true
    //============================================================================================================
    const isGroupChecked = (children) => {
        return children.every((c) => selectedColumns.includes(c));
    };

    //============================================================================================================
    // Checks if some children of a group are selected, and if so, returns true
    //============================================================================================================
    const isGroupIndeterminate = (children) => {
        const count = children.filter((c) => selectedColumns.includes(c)).length;
        return count > 0 && count < children.length;
    };

    //============================================================================================================
    // Toggles the visibility of a single column
    //============================================================================================================
    const toggleSingle = (col) => {
        const next = selectedColumns.includes(col) // If the column is already selected, remove it; otherwise, add it
            ? selectedColumns.filter((c) => c !== col)
            : [...selectedColumns, col];
        onChangeSelected(next); // Calls the onChangeSelected function with the updated list of selected columns
    };

    //============================================================================================================
    // Toggles the visibility of a group of columns (case the columns isn't a single column)
    //============================================================================================================
    const toggleGroup = (children, force) => {
        const currentlyOn = isGroupChecked(children); // Checks if the group is currently selected
        const shouldTurnOn = force ?? !currentlyOn; // If force is provided, use it; otherwise, toggle the current state

        let next = [...selectedColumns];
        // remove all children from the current selection
        next = next.filter((c) => !children.includes(c));
        // if shouldTurnOn, add all children to the selection
        if (shouldTurnOn) next = [...next, ...children];
        onChangeSelected(next);
    };

    //============================================================================================================
    // Renders the column menu
    //============================================================================================================
    return (
        <Menu
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            className="organizeMenuSearchTable"
            MenuListProps={{ disablePadding: true }}
        >
            <div className="stickyTopTable">
                <MenuItem disableRipple className="organizeMenuItem">
                    <InputBase
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search"
                        className="organizeInputBase"
                        value={query}
                        startAdornment={<InputAdornment position="start"><Search /></InputAdornment>}
                    />
                </MenuItem>
            </div>

            {/* For single columns*/}
            {filteredSingles.length > 0 && (
                <ListSubheader disableSticky className="menuItem" style={{ background: "var(--color-white)" }}>
                    {t("BaseColumns")}
                </ListSubheader>
            )}
            {filteredSingles.map((col, idx) => (
                <div key={col || idx} className="menuSwtchs menuItem">
                    <MSwitchButton
                        label={col}
                        color={switchColor}
                        checked={selectedColumns.includes(col)}
                        onClick={() => toggleSingle(col)}
                        labelColor={labelColor}
                    />
                </div>
            ))}

            {/* For Parent columns from Group */}
            {filteredGroups.length > 0 && (
                <ListSubheader disableSticky className="menuItem" style={{ background: "var(--color-white)" }}>
                    {t("Sources")}
                </ListSubheader>
            )}
            {filteredGroups.map((g, idx) => (
                <div key={g.label || idx} className="menuSwtchs menuItem">
                    <MSwitchButton
                        label={g.label}
                        color={switchColor}
                        // checks only if all children are selected
                        checked={isGroupChecked(g.children)}
                        // if you want an “indeterminate” UI, adapt the MSwitchButton to support partial state
                        onClick={() => toggleGroup(g.children)}
                        labelColor={labelColor}
                    />
                </div>
            ))}
        </Menu>
    );
}

ColumnMenu.propTypes = {
    anchorEl: PropTypes.any,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    singleColumns: PropTypes.array,           // ex.: ["Destination","Min Loads"]
    columnGroups: PropTypes.array,            // ex.: [{label:"Porto", children:["Porto Bid","Porto Best Bid"]}]
    selectedColumns: PropTypes.array.isRequired,
    onChangeSelected: PropTypes.func.isRequired,
    switchColor: PropTypes.string,
    labelColor: PropTypes.string,
};
